// Individual Targets' shared pure computation — extracted out of IndividualTargets.js (2026-09-10)
// so IndividualTargetsPanel.js (the Einstein compose panel) reuses the exact same ISO-week math
// and target-deviation logic instead of a second hand-copy. Same extraction pattern as
// gps/matchChronicShared.js.
import { getGPS, getPlayers, resolveTargetsBatch } from '../../api';

export const METRICS = [
  { key: 'td',   tKey: 'TD',   label: 'Total Distance',      unit: 'm', category: 'Endurance'     },
  { key: 'rd',   tKey: 'RD',   label: 'Run Dist. >14.4km/h', unit: 'm', category: 'Endurance'     },
  { key: 'hid',  tKey: 'HID',  label: 'HID >19.8km/h',       unit: 'm', category: 'High velocity' },
  { key: 'sd',   tKey: 'SD',   label: 'Sprint >25.2km/h',    unit: 'm', category: 'High velocity' },
  { key: 'sa',   tKey: 'SA',   label: 'Short Actions',       unit: '',  category: 'Small spaces'  },
  { key: 'expl', tKey: 'Expl', label: 'Explosive Dist.',     unit: 'm', category: 'Small spaces'  },
  { key: 'hmld', tKey: 'HMLD', label: 'HMLD',                unit: 'm', category: 'High velocity' },
];

export const COLORS = { above: '#7c3aed', green: '#10b981', amber: '#f59e0b', below: '#ef4444', none: 'var(--text-dimmed)' };

export function getStatus(val, t) {
  if (!t || (t.upper === 0 && t.lower === 0)) return 'none';
  if (val > t.upper) return 'above';
  if (val >= t.lower) return 'green';
  if (val >= t.lower * 0.9) return 'amber';
  return 'below';
}

export function getDeviation(actual, lower, upper) {
  if (lower === 0 && upper === 0) return null;
  if (actual >= lower && actual <= upper) return 0;
  if (actual < lower) return lower > 0 ? ((actual - lower) / lower) * 100 : null;
  return upper > 0 ? ((actual - upper) / upper) * 100 : null;
}

export function deviationColor(dev) {
  if (dev === null) return COLORS.none;
  if (dev > 0) return COLORS.above;
  if (dev === 0) return COLORS.green;
  if (dev >= -10) return COLORS.amber;
  return COLORS.below;
}

export function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

export function getISOWeekYear(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
}

export function toISOWeekStr(date) {
  return `${getISOWeekYear(date)}-W${String(getISOWeek(date)).padStart(2, '0')}`;
}

export function parseISOWeekToMonday(weekStr) {
  const [yearStr, weekPart] = weekStr.split('-W');
  const year = parseInt(yearStr);
  const week = parseInt(weekPart);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dow = jan4.getUTCDay() || 7;
  return new Date(Date.UTC(year, 0, 4 - dow + 1 + (week - 1) * 7));
}

export function getWeekRange(weekStr) {
  const mon = parseISOWeekToMonday(weekStr);
  const sun = new Date(mon);
  sun.setUTCDate(mon.getUTCDate() + 6);
  return { start: mon.toISOString().slice(0, 10), end: sun.toISOString().slice(0, 10) };
}

// One player's cumulative week totals (actual + summed target lower/upper bounds across every
// session that week) — the same reduction IndividualTargets.js's own `cumulative` memo does,
// given an already-resolved `resolvedBySession` map (session id -> {targets}).
export function computePlayerWeekSummary(playerId, isoWeek, sessions, resolvedBySession) {
  const { start, end } = getWeekRange(isoWeek);
  const weekSessionsForPlayer = (sessions || [])
    .filter(s => { const d = (s.session_date || '').slice(0, 10); return d >= start && d <= end; })
    .flatMap(s => {
      const pd = (s.players || []).find(p => String(p.player_id) === String(playerId));
      return pd ? [{ session: s, pd }] : [];
    });
  if (!weekSessionsForPlayer.length) return null;

  const n = v => parseFloat(v) || 0;
  const playerWeekSessions = weekSessionsForPlayer
    .map(({ session: s, pd }) => ({
      sessionId: s.id, date: (s.session_date || '').slice(0, 10),
      targets: resolvedBySession.get(String(s.id))?.targets || null,
      td: n(pd.td), rd: n(pd.rd), hid: n(pd.hid), sd: n(pd.sd), sa: n(pd.sa), expl: n(pd.expl), hmld: n(pd.hmld),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const actual = {}, tgtLower = {}, tgtUpper = {};
  METRICS.forEach(m => { actual[m.key] = 0; tgtLower[m.key] = 0; tgtUpper[m.key] = 0; });
  playerWeekSessions.forEach(s => {
    const tgt = s.targets;
    METRICS.forEach(m => {
      actual[m.key] += s[m.key] || 0;
      if (tgt?.[m.tKey]) { tgtLower[m.key] += tgt[m.tKey].lower; tgtUpper[m.key] += tgt[m.tKey].upper; }
    });
  });
  const targets = {};
  METRICS.forEach(m => { targets[m.key] = { lower: tgtLower[m.key], upper: tgtUpper[m.key] }; });
  const first = playerWeekSessions[0], last = playerWeekSessions[playerWeekSessions.length - 1];
  return { actual, targets, sessionCount: playerWeekSessions.length, dateFrom: first.date, dateTo: last.date };
}

// Resolves target-vs-actual summaries for N (playerId, isoWeek) requests in ONE
// resolveTargetsBatch call — the point of batching all of a compose response's panels together
// rather than one HTTP round trip per panel. Item keys are composite (`${playerId}|${isoWeek}::
// ${sessionId}`) since the same session id is shared across every player who trained that
// session — the plain session-id key IndividualTargets.js uses is safe there because it only ever
// resolves one player at a time; this doesn't have that luxury.
export async function fetchIndividualTargetsSummaries(requests, sessions) {
  const items = [];
  const weekSessionsByRequest = new Map();
  for (const { playerId, isoWeek } of requests) {
    const reqKey = `${playerId}|${isoWeek}`;
    const { start, end } = getWeekRange(isoWeek);
    const weekSessionsForPlayer = (sessions || [])
      .filter(s => { const d = (s.session_date || '').slice(0, 10); return d >= start && d <= end; })
      .flatMap(s => {
        const pd = (s.players || []).find(p => String(p.player_id) === String(playerId));
        return pd ? [{ session: s, pd }] : [];
      });
    weekSessionsByRequest.set(reqKey, weekSessionsForPlayer);
    for (const { session, pd } of weekSessionsForPlayer) {
      items.push({
        key: `${reqKey}::${session.id}`, playerId, position: pd.position || null,
        scenario: session.scenario || '', date: (session.session_date || '').slice(0, 10), dayLabel: pd.md_label || '',
      });
    }
  }

  const resolvedByItemKey = new Map();
  if (items.length) {
    const { results } = await resolveTargetsBatch(items);
    (results || []).forEach(r => resolvedByItemKey.set(r.key, r));
  }

  const summaries = new Map();
  for (const { playerId, isoWeek } of requests) {
    const reqKey = `${playerId}|${isoWeek}`;
    const weekSessionsForPlayer = weekSessionsByRequest.get(reqKey) || [];
    const resolvedBySession = new Map();
    weekSessionsForPlayer.forEach(({ session }) => {
      const r = resolvedByItemKey.get(`${reqKey}::${session.id}`);
      if (r) resolvedBySession.set(String(session.id), r);
    });
    summaries.set(reqKey, computePlayerWeekSummary(playerId, isoWeek, sessions, resolvedBySession));
  }
  return summaries;
}

// Shared reference-data fetch for IndividualTargetsPanel.js — team-wide sessions, one fetch
// feeding N per-player-per-week summaries (via fetchIndividualTargetsSummaries above).
export async function fetchIndividualTargetsPanelData() {
  const [sessions, players] = await Promise.all([getGPS(), getPlayers()]);
  return { sessions: sessions || [], players: players || [] };
}
